<template>
  <div class="flex flex-col gap-2">
    <empty-screen
      v-if="list.length === 0"
      class="my-5"
      mobile
      icon="design-arrow-up"
      :title="tr('Ad account required to start automating')"
      :flag-text="tr('Watch tutorial')"
      :description="tr('While your Meta profile is connected, we couldn\'t find any ad accounts linked to it. To use :app\'s automation features, you\'ll need an active ad account. Watch our tutorial video to learn how to create a new ad account or add an existing one to your Meta Business profile in just a few minutes.')"
      :path="tr('https://www.youtube.com/watch?v=bRPw0X_1MGc')"
    />
    <template v-else>
      <asset-select-list
        :search-term="searchTerm"
        :items="items"
        :searchable="list.length > 10"
        :disabled="disabled"
        :loading="loading"
        @update:search-term="(value) => searchTerm = value"
        @select="(item) => onSelect(item)"
      >
        <template #label="{ item }">
          <span class="block truncate text-sm font-semibold text-foreground">{{ item.name }}</span>
        </template>
      </asset-select-list>
      <empty-screen
        v-if="filteredList.length === 0"
        class="my-5"
        mobile
        icon="search"
        :title="tr('No results match your current search criteria')"
        :flag-text="tr('Modify search')"
        :description="tr('Currently there are no items that match your criteria. The items you\'re looking for might appear here later, or try different parameters to see currently available items.')"
      />
    </template>
  </div>
</template>

<script>
import { AD_ACCOUNT_STATUS } from '@/consts.js';
import AssetStatusMixin from '@/mixins/AssetStatus.js';
import SelectInputMixin from '@/mixins/SelectInput.js';
import AssetSelectList from '@/ui/components/AssetSelectList.vue';
import EmptyScreen from '@/ui/components/EmptyScreen.vue';

const PLATFORM = 'facebook';

export default {
  name: 'AdAccountSelectInput',
  components: {
    AssetSelectList,
    EmptyScreen,
  },
  mixins: [ AssetStatusMixin, SelectInputMixin ],
  computed: {
    items() {
      return [ ...this.filteredList ]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(item => ({
          key: item.external_id,
          value: item,
          selected: this.modelValue === item.external_id,
          disabled: this.isDisabled(item),
          loading: this.isLoading(item),
          status: item.status !== AD_ACCOUNT_STATUS.ACTIVE || this.modelValue === item.external_id ? this.statusLabel(item) : null,
          statusError: item.status != AD_ACCOUNT_STATUS.ACTIVE || item.lost_access === true,
        }));
    },
    list() {
      const adAccounts = [];
      const processedAdAccountIds = [];

      const companyAdAccounts = this.$root.currentCompany.ad_accounts.filter(adAcc => adAcc.platform === PLATFORM);
      companyAdAccounts.forEach(adAcc => {
        processedAdAccountIds.push(adAcc.external_id);
        adAccounts.push(adAcc);
      });

      const connectedProfiles = this.$root.loggedInUser.connected_profiles.filter(profile => profile.platform === PLATFORM);
      connectedProfiles.forEach(profile => {
        profile.details.ad_accounts.forEach(adAcc => {
          if (processedAdAccountIds.includes(adAcc.id)) {
            return;
          }

          adAcc.external_id = adAcc.id;
          adAccounts.push(adAcc);
          processedAdAccountIds.push(adAcc.external_id);
        });
      });

      return adAccounts;
    },
  },
  methods: {
    statusLabel(item) {
      if (item.status != AD_ACCOUNT_STATUS.ACTIVE) {
        return this.adAccountStatusLabel(item);
      }
      if (item.has_funding_source === false) {
        return this.tr('Missing payment method');
      }
      if (item.lost_access === true) {
        return this.tr('Access terminated');
      }

      return this.tr('Active');
    },
    isDisabled(item) {
      if (this.disabled) {
        return true;
      }

      if (this.modelValue === item.external_id) {
        return false;
      }

      return item.status !== AD_ACCOUNT_STATUS.ACTIVE
        && !this.adAccountStatusIsUnsettled(item)
        && !this.adAccountStatusIsPermissionError(item);
    },
    isLoading(item) {
      return this.loading && this.modelValue === item.external_id;
    },
  },
};
</script>
